import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Drill, TagPreference } from '@/components/drill-library/types/drill-types';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import DrillCard from '@/components/drill-library/DrillCard';
import { cn } from '@/lib/utils';
import Modal from '@/components/shared/Modal';
import { getDrillDomain, getTagValues } from '@/utils/drill-taxonomy';

interface DrillCardFlipperProps {
    drills: Drill[];
    tagPreferences: { [key: string]: TagPreference };
    onDrillSelect: (drillId: string) => void;
    onDrillDrag: (drillId: string) => void;
    onDrillDragEnd: () => void;
    visibleCards?: number; // Number of cards to show horizontally
    selectedDrillId?: string | null;
    onSelectDrillCandidate?: (drillId: string) => void;
    onTagClick?: (tag: string) => void;
    onAddDrill?: (drillId: string) => void;
    nextSlotLabel?: string;
}

const DrillCardFlipper: React.FC<DrillCardFlipperProps> = ({
    drills,
    tagPreferences,
    onDrillSelect,
    onDrillDrag,
    onDrillDragEnd,
    visibleCards = 3,
    selectedDrillId = null,
    onSelectDrillCandidate,
    onTagClick,
    onAddDrill,
    nextSlotLabel
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [previewDrill, setPreviewDrill] = useState<Drill | null>(null);
    const [showFullCard, setShowFullCard] = useState(false);

    // Helper: robust YouTube ID extraction; fallback to provided thumbnailUrl
    const getYouTubeId = (url?: string): string | null => {
        if (!url) return null;
        try {
            const u = new URL(url);
            if (u.hostname.includes('youtu.be')) {
                return u.pathname.replace('/', '') || null;
            }
            if (u.searchParams.get('v')) {
                return u.searchParams.get('v');
            }
            // Handle /embed/{id} or /v/{id}
            const parts = u.pathname.split('/').filter(Boolean);
            const maybeId = parts[parts.length - 1];
            return maybeId || null;
        } catch {
            return null;
        }
    };

    const domainColorMap: { [key: string]: string } = {
        Breathwork: 'bg-primary text-primary-foreground',
        Locomotion: 'bg-success text-success-foreground',
        Positional: 'bg-warning text-warning-foreground',
        Weightroom: 'bg-primary text-primary-foreground',
        Mobility: 'bg-accent text-accent-foreground',
        Stability: 'bg-warning text-warning-foreground',
        Strength: 'bg-error text-error-foreground'
    };

    // Reset to first drill when drills change (e.g., filtering)
    useEffect(() => {
        setCurrentIndex(0);
    }, [drills]);

    // Smooth scroll to current drill
    const scrollToCurrent = useCallback(() => {
        if (!containerRef.current) return;
        
        const drillElement = containerRef.current.querySelector(`[data-drill-index="${currentIndex}"]`);
        if (drillElement) {
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();
            const elementRect = drillElement.getBoundingClientRect();
            
            // Calculate the scroll position to center the element
            const scrollTop = elementRect.top - containerRect.top - (containerRect.height / 2) + (elementRect.height / 2);
            
            container.scrollTo({
                top: container.scrollTop + scrollTop,
                behavior: 'smooth'
            });
        }
    }, [currentIndex]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (isTransitioning) return;
            
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                moveUp();
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                moveDown();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, drills.length, isTransitioning]);

    // Mouse wheel navigation
    const handleWheel = useCallback((e: WheelEvent) => {
        e.preventDefault();
        
        if (isTransitioning) return;
        
        // Clear existing timeout
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        
        // Throttle wheel events
        scrollTimeoutRef.current = setTimeout(() => {
            if (e.deltaY > 0) {
                moveDown();
            } else if (e.deltaY < 0) {
                moveUp();
            }
        }, 50);
    }, [isTransitioning]);

    // Add wheel event listener
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('wheel', handleWheel, { passive: false });
            return () => container.removeEventListener('wheel', handleWheel);
        }
    }, [handleWheel]);


    const moveUp = () => {
        if (currentIndex > 0 && !isTransitioning) {
            setIsTransitioning(true);
            setCurrentIndex(currentIndex - 1);
            setTimeout(() => {
                scrollToCurrent();
                setIsTransitioning(false);
            }, 100);
        }
    };

    const moveDown = () => {
        if (currentIndex < drills.length - 1 && !isTransitioning) {
            setIsTransitioning(true);
            setCurrentIndex(currentIndex + 1);
            setTimeout(() => {
                scrollToCurrent();
                setIsTransitioning(false);
            }, 100);
        }
    };

    const jumpToCard = (index: number) => {
        if (index !== currentIndex && !isTransitioning) {
            setIsTransitioning(true);
            setCurrentIndex(index);
            setTimeout(() => {
                scrollToCurrent();
                setIsTransitioning(false);
            }, 100);
        }
    };

    if (drills.length === 0) {
        return (
            <div className="h-full flex items-center justify-center">
                <p className="text-muted-foreground">No drills found</p>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Navigation Controls */}
            <div className="flex justify-between items-center p-3 border-b bg-muted/30">
                <div className="flex items-center gap-2">
                    <Button
                        onClick={moveUp}
                        disabled={currentIndex === 0 || isTransitioning}
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                    >
                        <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={moveDown}
                        disabled={currentIndex === drills.length - 1 || isTransitioning}
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                    >
                        <ChevronDown className="h-4 w-4" />
                    </Button>
                </div>
                <div className="text-sm text-muted-foreground">
                    {currentIndex + 1} of {drills.length}
                </div>
                <div className="text-xs text-muted-foreground">
                    Use ↑↓ or scroll wheel
                </div>
            </div>

            {/* Fixed Viewport with Drill Cards */}
            <div className="flex-1 relative overflow-hidden">
                {/* Horizontal Row of Drill Cards - Under Header */}
                <div className="absolute top-0 left-0 right-0 z-10">
                    <div className={cn(
                        "flex h-full rounded transition-all duration-200",
                        "gap-0.5 p-0.5 md:gap-1 md:p-1",
                        "border-2 border-primary/30",
                        "hover:border-primary/50"
                    )}>
                        {drills.slice(currentIndex, currentIndex + visibleCards).map((drill, index) => {
                            const actualIndex = currentIndex + index;
                            const isCurrent = actualIndex === currentIndex;
                            
                            return (
                                <div 
                                    key={drill.id}
                                    className={cn("flex-1 min-w-0", selectedDrillId === drill.id && "ring-2 ring-white/90 rounded-md")}
                                >
                                    <div 
                                        className="relative w-full aspect-[3/4]"
                                        draggable
                                        onDragStart={() => onDrillDrag(drill.id)}
                                        onDragEnd={onDrillDragEnd}
                                        onClick={() => {
                                            if (selectedDrillId === drill.id) {
                                                setPreviewDrill(drill);
                                                setShowFullCard(true);
                                            } else {
                                                onSelectDrillCandidate?.(drill.id);
                                            }
                                        }}
                                        onTouchStart={() => onDrillDrag(drill.id)}
                                    >
                                        <Card className={cn(
                                            "absolute inset-0 transition-all duration-200 cursor-pointer hover:shadow-md rounded-md overflow-hidden",
                                            "border-primary shadow-lg"
                                        )}>
                                            <CardContent className="p-0 h-full">
                                                {/* Drill Card with enforced 9:16 overall and edge-to-edge 16:9 thumbnail */}
                                                <div className="h-full flex flex-col overflow-hidden">
                                                    {/* Edge-to-edge thumbnail with 16:9 aspect */}
                                                    <div className="w-full aspect-video bg-muted overflow-hidden cursor-pointer flex-shrink-0"
                                                         onClick={(e) => { e.stopPropagation(); if (selectedDrillId === drill.id) { setPreviewDrill(drill); setShowFullCard(true); } else { onSelectDrillCandidate?.(drill.id); } }}
                                                         title="Open details">
                                                        {(() => {
                                                            const ytId = getYouTubeId(drill.videoUrl);
                                                            const thumb = ytId
                                                                ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
                                                                : (drill.thumbnailUrl || null);
                                                            if (thumb) {
                                                                return (
                                                                    <img
                                                                        src={thumb}
                                                                        alt={drill.name}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                );
                                                            }
                                                            return (
                                                                <div className="w-full h-full bg-muted grid place-items-center">
                                                                    <span className="text-xs text-muted-foreground">No Preview</span>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>

                                                    {/* Drill Info - responsive, compact */}
                                                    <div className="px-2 pt-2 pb-1 flex-1 flex flex-col gap-1.5 min-h-0">
                                                        {/* Title */}
                                                        <h4 className="font-medium break-words whitespace-normal leading-tight line-clamp-2 text-sm xl:text-base flex-shrink-0 text-card-foreground" title={drill.name}>
                                                            {drill.name}
                                                        </h4>

                                                        {/* Tags - Max 3 tags visible */}
                                                        <div className="flex gap-1 flex-wrap items-start flex-shrink-0">
                                                            {(() => {
                                                                const order = [
                                                                    'Domain',
                                                                    'Phase',
                                                                    'Biomechanical Intent',
                                                                    'Movement Pattern',
                                                                    'Movement Pattern (Broad)',
                                                                    'Gait phase Target',
                                                                    'Gait Phase Target',
                                                                    'Body postural',
                                                                    'Body postural/lateralisation',
                                                                    'Body postural / lateralisation',
                                                                    'Lateralisation'
                                                                ];
                                                                const tags = drill.tags || {};
                                                                const available = order.filter(k => tags[k] !== undefined);
                                                                const selected = available.slice(0, 3); // Enforce max 3 tags
                                                                return selected.map((k, idx) => {
                                                                    const val = tags[k];
                                                                    const text = Array.isArray(val) ? (val[0] as string) : (val as string);
                                                                    if (!text) return null;
                                                                    const isDomain = k === 'Domain';
                                                                    const domainClass = isDomain ? domainColorMap[text] || 'bg-primary text-primary-foreground' : '';
                                                                    return (
                                                                        <Badge 
                                                                            key={`${k}-${idx}`} 
                                                                            onClick={(e) => { e.stopPropagation(); onTagClick?.(text); }} 
                                                                            variant={isDomain ? "default" : "secondary"} 
                                                                            className={cn(
                                                                                "cursor-pointer text-[10px] xl:text-[11px] px-1.5 py-0.5 truncate max-w-full",
                                                                                isDomain && domainClass
                                                                            )}
                                                                            title={text}
                                                                        >
                                                                            {text}
                                                                        </Badge>
                                                                    );
                                                                });
                                                            })()}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Footer with drill number */}
                                                    <div className="px-2 pb-1.5 border-t border-border/50 mt-auto">
                                                        <div className="text-[10px] text-muted-foreground text-center pt-1">
                                                            #{actualIndex + 1}
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Scrollable Background List */}
                <div 
                    ref={containerRef}
                    className="absolute inset-0 overflow-y-auto"
                >
                    <div className="space-y-1 p-2">
                        {/* Spacer to account for horizontal row at top */}
                        <div className="h-40" />
                        
                        {drills.map((drill, index) => {
                            const isCurrent = index === currentIndex;
                            const isNearby = Math.abs(index - currentIndex) <= 2;
                            
                            return (
                                <div
                                    key={drill.id}
                                    data-drill-index={index}
                                    className={cn(
                                        "transition-all duration-200 cursor-pointer",
                                        isCurrent && "opacity-0", // Hide current drill in background
                                        !isCurrent && "hover:opacity-100"
                                    )}
                                    onClick={() => jumpToCard(index)}
                                >
                                    {/* Compact preview for all drills */}
                                    <Card className={cn(
                                        "transition-all duration-200",
                                        isNearby ? "opacity-30" : "opacity-20",
                                        "hover:opacity-60 hover:shadow-sm"
                                    )}>
                                        <CardContent className="p-3">
                                            <div className="flex items-center gap-3">
                                                {/* Thumbnail */}
                                                <div className="w-12 h-8 flex-shrink-0 rounded overflow-hidden bg-muted">
                                                    {drill.videoUrl ? (
                                                        <img
                                                            src={`https://img.youtube.com/vi/${drill.videoUrl.split('v=')[1]?.split('&')[0]}/mqdefault.jpg`}
                                                            alt={drill.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full bg-muted flex items-center justify-center">
                                                            <span className="text-xs text-muted-foreground">No Preview</span>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {/* Drill Info */}
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-medium text-sm truncate">{drill.name}</h4>
                                                    <p className="text-xs text-muted-foreground truncate">{getDrillDomain(drill)}</p>
                                                </div>
                                                
                                                {/* Tags */}
                                                <div className="flex gap-1">
                                                    {getTagValues(drill, 'Domain')
                                                        .slice(0, 1)
                                                        .map((tag, tagIndex) => (
                                                            <Badge key={`${drill.id}-${tag}-${tagIndex}`} variant="secondary" className="text-xs px-1 py-0">
                                                                {tag}
                                                            </Badge>
                                                        ))}
                                                </div>
                                                
                                                {/* Position indicator */}
                                                <div className="text-xs text-muted-foreground">
                                                    #{index + 1}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            );
                        })}
                        
                        {/* Bottom spacer */}
                        <div className="h-16" />
                    </div>
                </div>
            </div>

            {/* Full Drill Card Modal with Add-to-slot action */}
            {previewDrill && showFullCard && (
                <Modal isOpen={!!previewDrill} onClose={() => { setPreviewDrill(null); setShowFullCard(false); }} title={previewDrill.name} maxWidth="max-w-4xl">
                    <div className="space-y-4">
                        {/* Video */}
                        {(() => {
                            const ytId = getYouTubeId(previewDrill.videoUrl);
                            const src = ytId ? `https://www.youtube.com/embed/${ytId}` : undefined;
                            return src ? (
                                <iframe
                                    src={src}
                                    className="w-full aspect-video"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    title={previewDrill.name}
                                />
                            ) : (
                                <div className="w-full aspect-video grid place-items-center text-muted-foreground">No Preview</div>
                            );
                        })()}

                        {/* Details */}
                        <div className="space-y-2">
                            <div>
                                <h4 className="text-sm font-semibold mb-2">Tags</h4>
                                <div className="flex flex-wrap gap-2">
                                    {(() => {
                                        const all = Object.values(previewDrill.tags || {}).flatMap(v => Array.isArray(v) ? v : [v]).filter(Boolean) as string[];
                                        const half = Math.max(1, Math.ceil(all.length * 0.5));
                                        return all.slice(0, half).map((t, i) => (
                                            <Badge key={i} onClick={() => onTagClick?.(t)} className="cursor-pointer" variant="secondary">{t}</Badge>
                                        ));
                                    })()}
                                </div>
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold mb-1">Description</h4>
                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {previewDrill.description || 'No description available.'}
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <Button onClick={() => { onAddDrill?.(previewDrill.id); setPreviewDrill(null); setShowFullCard(false); }}>
                                {nextSlotLabel ? `Add to ${nextSlotLabel}` : 'Add Drill to Slot'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Pagination Dots */}
            <div className="p-3 border-t bg-muted/30">
                <div className="flex justify-center gap-1 flex-wrap">
                    {drills.slice(0, 15).map((_, index) => (
                        <button
                            key={index}
                            onClick={() => jumpToCard(index)}
                            className={cn(
                                "w-2 h-2 rounded-full transition-all duration-200",
                                index === currentIndex 
                                    ? "bg-primary scale-125" 
                                    : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                            )}
                        />
                    ))}
                    {drills.length > 15 && (
                        <span className="text-xs text-muted-foreground ml-2">
                            +{drills.length - 15} more
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DrillCardFlipper;

